import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, AlertTriangle, FileText, Search, CheckCircle, 
  TrendingUp, Lock, Users, ArrowRight, Globe, Smartphone, 
  Briefcase, Bitcoin, Heart, CreditCard, Mail, Phone, User,
  Database, ChevronRight, Activity, Eye, Zap, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import ScamIntelligenceTicker from '../components/ScamIntelligenceTicker';

// Check type icons
const CheckTypeIcon = ({ type }) => {
  const icons = {
    domain: Globe,
    company: Briefcase,
    phone: Phone,
    whatsapp: Smartphone,
    email: Mail,
    social: User,
    wallet: Bitcoin
  };
  const Icon = icons[type] || Globe;
  return <Icon className="w-5 h-5" />;
};

const Home = () => {
  const navigate = useNavigate();
  const [checkType, setCheckType] = useState('domain');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const checkTypes = [
    { id: 'domain', label: 'Website / Domain', placeholder: 'example.com', icon: Globe },
    { id: 'company', label: 'Company Name', placeholder: 'ABC Trading Ltd', icon: Briefcase },
    { id: 'phone', label: 'Phone Number', placeholder: '+44 7700 900123', icon: Phone },
    { id: 'whatsapp', label: 'WhatsApp Number', placeholder: '+44 7700 900123', icon: Smartphone },
    { id: 'email', label: 'Email Address', placeholder: 'contact@example.com', icon: Mail },
    { id: 'social', label: 'Social Media Profile', placeholder: '@username or profile URL', icon: User },
    { id: 'wallet', label: 'Crypto Wallet', placeholder: '0x1234...abcd', icon: Bitcoin }
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    
    // Create URL-safe slug from search query
    const slug = searchQuery
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    
    // Navigate to payment unlock page instead of results
    setTimeout(() => {
      navigate(`/scam-check/unlock?type=${checkType}&entity=${encodeURIComponent(slug)}&query=${encodeURIComponent(searchQuery)}`);
    }, 500);
  };

  const scamCategories = [
    { icon: Heart, label: 'Romance Scams', slug: 'romance-scams', color: 'text-pink-500', bg: 'bg-pink-500/10' },
    { icon: Bitcoin, label: 'Crypto Investment Scams', slug: 'crypto-investment-scams', color: 'text-orange-500', bg: 'bg-orange-500/10' },
    { icon: Briefcase, label: 'Job Offer Scams', slug: 'job-offer-scams', color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { icon: Smartphone, label: 'WhatsApp Scams', slug: 'whatsapp-scams', color: 'text-green-500', bg: 'bg-green-500/10' },
    { icon: Globe, label: 'Fake Company Scams', slug: 'fake-company-scams', color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { icon: Mail, label: 'Phishing Websites', slug: 'phishing-websites', color: 'text-red-500', bg: 'bg-red-500/10' },
    { icon: TrendingUp, label: 'Investment Fraud', slug: 'investment-fraud', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
    { icon: CreditCard, label: 'Loan Scams', slug: 'loan-scams', color: 'text-cyan-500', bg: 'bg-cyan-500/10' }
  ];

  const investigationServices = [
    {
      title: 'Instant Scam Check',
      price: '£4.99',
      description: 'Basic automated fraud risk scan with key indicators.',
      features: ['Fraud Risk Score', 'Basic Risk Indicators', 'AI Summary Report', 'Instant Results'],
      cta: 'Run Check',
      href: '/scam-check',
      highlight: false,
      color: 'blue'
    },
    {
      title: 'Professional Fraud Assessment',
      price: '£49.00',
      description: 'Enhanced OSINT review with deeper entity analysis.',
      features: ['OSINT Intelligence Review', 'Digital Footprint Analysis', 'Entity Verification', 'Evidence Summary'],
      cta: 'Start Assessment',
      href: '/investigations',
      highlight: true,
      color: 'cyan'
    },
    {
      title: 'Investigation Dossier',
      price: '£1,250.00',
      description: 'Full manual fraud investigation with legal-ready report.',
      features: ['Deep Investigation', 'Timeline Reconstruction', 'Legal-Ready Evidence', 'Expert Consultation'],
      cta: 'Learn More',
      href: '/investigations',
      highlight: false,
      color: 'purple'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Helmet>
        <title>Check If a Website, Company or Person Is a Scam | ActionFraudOne</title>
        <meta name="description" content="ActionFraudOne helps individuals and businesses verify entities, detect scams, and investigate suspicious activity using fraud intelligence analysis." />
        <meta name="keywords" content="scam checker, fraud investigation, is it a scam, verify company, scam database, fraud intelligence" />
      </Helmet>

      {/* Live Scam Intelligence Ticker */}
      <div className="pt-20">
        <ScamIntelligenceTicker />
      </div>

      {/* Hero Section */}
      <section className="relative pt-8 pb-16 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse at 50% 0%, rgba(30, 64, 175, 0.12) 0%, transparent 60%)'}} />
        <div className="absolute inset-0 network-grid opacity-20" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            {/* Brand Name + Tagline */}
            <div className="flex flex-col items-center mb-10">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 tracking-tight">
                <span className="text-white">Action</span>
                <span className="text-[#3b82f6]">FraudOne</span>
                <sup className="text-[0.35em] text-blue-300/75 ml-0.5 font-normal">™</sup>
              </h2>
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1e40af]/15 border border-[#3b82f6]/40 rounded-full">
                <Activity className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-semibold tracking-wide">Verify. Protect. Take Action.</span>
              </div>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-6 leading-tight tracking-tight">
              Check If a Website, Company
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 mt-2">
                or Person Is a Scam
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-400 text-center mb-10 max-w-3xl mx-auto leading-relaxed">
              ActionFraudOne helps individuals and businesses verify entities, detect scams, 
              and investigate suspicious activity using fraud intelligence analysis.
            </p>

            {/* Scam Intelligence Engine - with anchor ID */}
            <div id="scam-checker-section" className="max-w-4xl mx-auto scroll-mt-24">
              <Card className="bg-[#0d1d32]/80 backdrop-blur-xl border border-[#1e40af]/30 shadow-2xl shadow-blue-900/20">
                <CardContent className="p-8 md:p-10">
                  {/* Check Type Selector */}
                  <div className="flex flex-wrap gap-2 mb-8 justify-center">
                    {checkTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setCheckType(type.id)}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          checkType === type.id
                            ? 'bg-[#1e40af] text-white shadow-lg shadow-blue-500/30'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                        }`}
                        data-testid={`check-type-${type.id}`}
                      >
                        <type.icon className="w-4 h-4" />
                        <span className="hidden sm:inline">{type.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Search Form */}
                  <form onSubmit={handleSearch} className="relative">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1 relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                          <CheckTypeIcon type={checkType} />
                        </div>
                        <Input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={checkTypes.find(t => t.id === checkType)?.placeholder}
                          className="w-full pl-12 pr-4 py-6 bg-[#0a1628] border-[#1e40af]/30 text-white placeholder:text-gray-600 text-lg rounded-xl focus:border-[#1e40af] focus:ring-2 focus:ring-[#1e40af]/20"
                          data-testid="scam-checker-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        disabled={isSearching || !searchQuery.trim()}
                        className="bg-gradient-to-r from-[#1e40af] to-cyan-600 hover:from-[#1e3a8a] hover:to-cyan-500 text-white font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-blue-500/30 transition-all duration-200"
                        data-testid="scam-checker-submit"
                      >
                        {isSearching ? (
                          <span className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Checking...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Search className="w-5 h-5" />
                            Check Now
                          </span>
                        )}
                      </Button>
                    </div>
                  </form>

                  {/* Trust Indicators */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8 pt-6 border-t border-[#1e40af]/15">
                    <div className="flex items-center gap-3 p-3 bg-[#0a1628]/50 rounded-lg">
                      <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Activity className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">24/7 Fraud Check Access</p>
                        <p className="text-gray-500 text-xs">Run checks any time, instant results</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-[#0a1628]/50 rounded-lg">
                      <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Database className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">Multi-Entity Verification</p>
                        <p className="text-gray-500 text-xs">Websites, companies, phones & more</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-[#0a1628]/50 rounded-lg">
                      <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">Investigation Support</p>
                        <p className="text-gray-500 text-xs">Escalate to professional analysis</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link to="/report-a-scam">
                <Button variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 px-6 py-3">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Report a Scam
                </Button>
              </Link>
              <Link to="/investigations">
                <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 px-6 py-3">
                  <FileText className="w-4 h-4 mr-2" />
                  Investigation Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-8 bg-[#0d1d32] border-y border-[#1e40af]/20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { icon: Shield, label: 'Fraud Intelligence Analysis' },
              { icon: Search, label: 'Entity Verification' },
              { icon: Lock, label: 'Secure & Confidential' },
              { icon: FileText, label: 'Professional Reports' },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 justify-center">
                <div className="w-10 h-10 bg-[#1e40af]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-gray-400 text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-[#0a1628] relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How Our Intelligence Platform Works</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Advanced fraud detection powered by multi-source intelligence analysis.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { step: '01', title: 'Enter Query', description: 'Submit any website, company, phone number, email, or crypto wallet for analysis.', icon: Search },
              { step: '02', title: 'Intelligence Scan', description: 'Our systems check multiple fraud databases and intelligence sources.', icon: Database },
              { step: '03', title: 'Risk Assessment', description: 'AI analyzes patterns and generates a comprehensive fraud risk score.', icon: Activity },
              { step: '04', title: 'Get Results', description: 'Receive detailed findings with risk indicators and recommended actions.', icon: Eye }
            ].map((item, index) => (
              <div key={index} className="relative">
                <Card className="bg-[#0d1d32] border-[#1e40af]/20 hover:border-[#1e40af]/40 transition-all duration-300 h-full">
                  <CardContent className="p-6">
                    <div className="text-5xl font-bold text-[#1e40af]/20 mb-4">{item.step}</div>
                    <div className="w-12 h-12 bg-[#1e40af]/10 rounded-xl flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-6 h-6 text-[#1e40af]/50" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scam Categories */}
      <section className="py-24 bg-[#0d1d32] relative">
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Fraud Database</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Browse our comprehensive database of scam types, warning signs, and prevention advice.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {scamCategories.map((category, index) => (
              <Link key={index} to={`/scams/${category.slug}`}>
                <Card className="bg-[#0a1628] border-[#1e40af]/20 hover:border-[#1e40af]/40 h-full cursor-pointer group transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className={`w-14 h-14 ${category.bg} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <category.icon className={`w-7 h-7 ${category.color}`} />
                    </div>
                    <h3 className="text-white font-semibold text-sm group-hover:text-blue-400 transition-colors">{category.label}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/scam-database">
              <Button variant="outline" className="border-[#1e40af]/50 text-blue-400 hover:bg-[#1e40af]/10 px-6 py-3">
                View Full Fraud Database <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Investigation Services */}
      <section className="py-24 bg-[#0a1628]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Investigation Services</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From quick scam checks to comprehensive fraud investigations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {investigationServices.map((service, index) => (
              <Card key={index} className={`bg-[#0d1d32] border-[#1e40af]/20 relative overflow-hidden transition-all duration-300 hover:border-[#1e40af]/40 ${service.highlight ? 'ring-2 ring-cyan-500/50' : ''}`}>
                {service.highlight && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1e40af] to-cyan-500" />
                )}
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className={`text-3xl font-bold ${service.color === 'cyan' ? 'text-cyan-400' : service.color === 'purple' ? 'text-purple-400' : 'text-blue-400'}`}>
                      {service.price}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                        <CheckCircle className={`w-4 h-4 ${service.color === 'cyan' ? 'text-cyan-500' : service.color === 'purple' ? 'text-purple-500' : 'text-blue-500'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.href}>
                    <Button className={`w-full ${
                      service.highlight 
                        ? 'bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400' 
                        : 'bg-[#1e40af] hover:bg-[#1e3a8a]'
                    } text-white font-bold py-3`}>
                      {service.cta} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/investigations">
              <Button variant="outline" className="border-[#1e40af]/50 text-blue-400 hover:bg-[#1e40af]/10 px-6 py-3">
                View All Services & Pricing <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose ActionFraudOne */}
      <section className="py-24 bg-[#0d1d32]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Choose ActionFraudOne</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-10 max-w-4xl mx-auto">
            {[
              {
                icon: Database,
                title: 'Intelligence-Led Analysis',
                description: 'Multi-source intelligence gathering with access to fraud databases, OSINT tools, and pattern analysis.'
              },
              {
                icon: FileText,
                title: 'Professional Evidence Reports',
                description: 'Detailed documentation suitable for legal proceedings, police reporting, or financial institution disputes.'
              },
              {
                icon: Lock,
                title: 'Secure & Confidential',
                description: 'All submissions handled with strict confidentiality. Your data is encrypted and protected.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#1e40af]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-r from-[#1e40af]/20 via-cyan-500/10 to-[#1e40af]/20 border-y border-[#1e40af]/20">
        <div className="container mx-auto px-4 text-center">
          <Target className="w-16 h-16 text-blue-400 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Protect Yourself From Fraud Today
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
            Don't fall victim to scams. Use our intelligence platform to verify suspicious entities 
            and protect your finances.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/scam-check">
              <Button size="lg" className="bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold px-8 py-6 text-lg shadow-lg shadow-blue-500/30">
                <CheckCircle className="mr-2" size={20} />
                Start Scam Check - £4.99
              </Button>
            </Link>
            <Link to="/report-a-scam">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                <AlertTriangle className="mr-2" size={20} />
                Report a Scam
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
